import { Popup } from 'react-map-gl/maplibre';

import { formatDistance } from '@/utils/formatDistance';

interface RadialDistanceLabelProps {
    /** [longitude, latitude] of the measurement centre. */
    centre: [number, number];
    radiusMeters: number;
}

/**
 * The radial tool's live distance label: a dark pill (the tooltip inversion tokens)
 * anchored just above the measurement centre, so it stays put while the radius line
 * is dragged instead of chasing it. Positioned by MapLibre's own `<Popup>`, which
 * re-anchors it on every pan/zoom; chrome is stripped via `.orbit-hover-popup`
 * (which also applies the Poppins app sans) and `--passive` keeps it from ever
 * capturing the pointer mid-drag. `tabular-nums` and the fixed decimals in
 * {@link formatDistance} keep its width steady while dragging.
 */
export function RadialDistanceLabel({ centre, radiusMeters }: RadialDistanceLabelProps) {
    return (
        <Popup
            longitude={centre[0]}
            latitude={centre[1]}
            anchor="bottom"
            closeButton={false}
            closeOnClick={false}
            closeOnMove={false}
            focusAfterOpen={false}
            offset={12}
            maxWidth="none"
            className="orbit-hover-popup orbit-hover-popup--passive"
        >
            {/* Poppins 400 / text-sm / leading-5 / tracking-normal per the design. */}
            <div className="bg-foreground text-background pointer-events-none relative rounded-md px-3 py-1.5 text-sm leading-5 font-normal tracking-normal whitespace-nowrap tabular-nums shadow-lg">
                Radial Distance &bull; {formatDistance(radiusMeters)}
                {/* The tooltip arrow, drawn exactly as shared-ui's TooltipContent draws its
                    Base UI Arrow (a 10px rotated square with softened corners), so this
                    MapLibre-positioned label reads as the same tooltip family — the popup's
                    own tip stays stripped by .orbit-hover-popup. */}
                <span
                    aria-hidden="true"
                    className="bg-foreground absolute -bottom-[3px] left-1/2 size-2.5 -translate-x-1/2 rotate-45 rounded-[2px]"
                />
            </div>
        </Popup>
    );
}
